import React, { useEffect, useMemo } from 'react';
import { Provider, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import { useTrackProductViewed } from './features/pdp/hooks/useTrackProductViewed';
import { pdpStoreFactory } from './store/pdpStore';
import { setView } from './actions/pdp.actions';
import '../../axios.config';
import LoadingSpinner from './components/tailwind/LoadingSpinner';
import { getPDPV2Data } from './features/pdp/pdp-v2.data';
import { useSite } from '@hooks';
import { PDPViews, PDP_V2_ITEMS } from '@types';
import ProductDetails from '@/features/pdp/components/ProductDetails';
import ProductWithBundle from '@/features/pdp/components/ProductWithBundle';
import { QueryClient, QueryClientProvider } from 'react-query';
import ErrorBoundary from './components/tailwind/ErrorBoundary';
import { createRoot } from 'react-dom/client';
import useABTests from './hooks/useABTests';
import { BrowserRouter } from 'react-router-dom';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PDPRoot = ({ klaviyoConfigured, crumbs, site: siteAttr }) => {
  useABTests();
  const dispatch = useAppDispatch();
  useEffect(() => {
    document.querySelector('#product-details-container').removeAttribute('data-json-item');
    document.querySelector('#product-details-container').removeAttribute('data-site');
    document.querySelector('#product-details-container').removeAttribute('data-klaviyo-configured');
    document.querySelector('#product-details-container').removeAttribute('data-site');
    window.prerenderReady = true;
  }, []);
  const pdpItem = useSelector((state) => state.pdpItem);
  const { details, site, inlineBundleData, view } = pdpItem;
  useSite(siteAttr);

  const parsedCrumbs = useMemo(
    () =>
      crumbs &&
      (JSON.parse(crumbs) || []).map((crumb) => {
        return {
          text: crumb.text,
          url: crumb.link,
          isInternalLink: false,
        };
      }),
    [crumbs]
  );

  useTrackProductViewed(details, site);

  useEffect(() => {
    const handleUrlChange = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const customQueryParam = searchParams.get('custom');
      const designQueryParam = searchParams.get('design');
      const viewQueryParam = searchParams.get('view');

      if (!!customQueryParam) {
        dispatch(setView(PDPViews.BULK));
      } else if (!!designQueryParam) {
        dispatch(setView(PDPViews.DESIGN));
      } else if (!!viewQueryParam) {
        if (viewQueryParam === PDPViews.DESIGN) dispatch(setView(PDPViews.DESIGN));
        else if (viewQueryParam === PDPViews.BULK) dispatch(setView(PDPViews.BULK));
        else if (viewQueryParam === PDPViews.ASSET_PREVIEW) dispatch(setView(PDPViews.ASSET_PREVIEW));
        else if (viewQueryParam === PDPViews.DEFAULT) dispatch(setView(PDPViews.DEFAULT));
      } else {
        dispatch(setView(PDPViews.DEFAULT));
      }
    };

    window.addEventListener('popstate', handleUrlChange);

    handleUrlChange();

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [dispatch]);

  return (
    <React.Suspense fallback={<LoadingSpinner />}>
      {inlineBundleData ? (
        <ProductWithBundle
          klaviyoConfigured={klaviyoConfigured}
          v2Data={getPDPV2Data(details?.attributes?.typeId)}
          inlineBundleData={inlineBundleData}
          shopItemId={details.id}
          isV2={PDP_V2_ITEMS.includes(details?.attributes?.typeId)}
          crumbs={parsedCrumbs}
        />
      ) : (
        <ProductDetails
          klaviyoConfigured={klaviyoConfigured}
          v2Data={getPDPV2Data(details?.attributes?.typeId)}
          isV2={PDP_V2_ITEMS.includes(details?.attributes?.typeId)}
          crumbs={parsedCrumbs}
        />
      )}
    </React.Suspense>
  );
};

const rootEl = document.getElementById('product-details-container');

if (rootEl) {
  createRoot(rootEl).render(
    <ErrorBoundary>
      <Provider store={pdpStoreFactory(JSON.parse(rootEl.dataset.jsonItem || '{}'))}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <PDPRoot {...rootEl.dataset} />
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}
